import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import api from "../services/api";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, ToggleButton, ToggleButtonGroup } from "@mui/material";
import theme from "../theme";
import { blue, grey } from "@mui/material/colors";
import moment from "moment";

const TIME_RANGES = [
  { label: '5m', seconds: 300 },
  { label: '15m', seconds: 900 },
  { label: '30m', seconds: 1800 },
  { label: '1h', seconds: 3600 },
  { label: '3h', seconds: 10800 },
];

const convertUnixToDateTime = (unixTimestamp) => moment.unix(unixTimestamp).format('HH:mm:ss');

function filterByTimeRange(data, rangeSeconds) {
  if (!data || data.length === 0) return data;
  const now = Math.floor(Date.now() / 1000);
  const cutoff = now - rangeSeconds;
  return data.filter(item => item.clock >= cutoff);
}

export default function Graph({ itemid, open, codEmpresa, handlerClose }) {
  const [graphData, setGraphData] = useState([]);
  const [isGBPs, setIsGBPs] = useState(true);
  const [timeRange, setTimeRange] = useState('3h');

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          {payload?.map((v, i) =>
            <p key={i} className="label">{isGBPs ? v.value + " Gbps" : v.value}</p>
          )}
          <p className="label">{convertUnixToDateTime(payload[0].payload.clock)} </p>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    if (!open) {
      setGraphData([]);
      return;
    }

    const fetchData = () => {
      api.post('map/items-data', {
        cod_empresa: codEmpresa,
        itemid: itemid,
      }).then((res) => {
        const check = res.data.some((el) => Number(el.value) > 10000000);
        setIsGBPs(check);
        const tmp_data = res.data.map((item) => {
          const value = Number(item.value);
          return {
            ...item,
            value: check ? (value / 1000000000).toFixed(2) : value,
            clock: Number(item.clock),
          };
        });

        setGraphData(tmp_data.sort((a, b) => a.clock - b.clock));
      });
    };

    fetchData();
    const intervalId = setInterval(fetchData, 30000);
    return () => clearInterval(intervalId);
  }, [itemid, open]);

  const rangeObj = TIME_RANGES.find(r => r.label === timeRange) || TIME_RANGES[4];
  const filteredData = filterByTimeRange(graphData, rangeObj.seconds);

  return (
    <Dialog open={open} fullWidth={true} maxWidth="lg">
      <Box component="form" sx={{ mt: 1 }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={(e, val) => { if (val) setTimeRange(val); }}
              size="small"
            >
              {TIME_RANGES.map(r => (
                <ToggleButton key={r.label} value={r.label} sx={{ fontSize: 12, px: 1.5 }}>
                  {r.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <AreaChart
              width={1200}
              height={300}
              data={filteredData}
              margin={{
                top: 20,
                right: 50,
                left: 20,
                bottom: 20,
              }}
            >
              <CartesianGrid fillOpacity={0.6} />
              <XAxis
                dataKey="clock"
                tickFormatter={convertUnixToDateTime}
                allowDataOverflow={false}
              />
              <YAxis />
              <Tooltip
                contentStyle={{ backgroundColor: grey[900] }}
                itemStyle={{ color: grey[100] }}
                content={CustomTooltip}
              />
              <Area
                connectNulls
                type="linear"
                unit={!isGBPs ? '' : 'Gbps'}
                strokeWidth={2}
                dataKey="value"
                name={"name"}
                stroke={blue[500]}
              />
            </AreaChart>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: theme.palette.text.primary }} onClick={handlerClose}>Fechar</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
